import React, { useState } from "react"
import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Button,
  Label,
  Modal,
  Table,
  Badge,
} from "reactstrap"
import moment from "moment"
import { get, post } from "utils/apiClient"

// Reads the stop log (appt_req_stop_log) - appointment requests captured from the auto and
// QSND paths. Search by a single box (order/location/customer/reference) + trigger time;
// re-trigger replays the captured metadata so the rules engine reruns the same rules.
const TRIGGER_MODES = [
  { value: "ORIGINAL", label: "Original (as captured)" },
  { value: "AUTO_ON_NEW_STOP", label: "Automation (new stop)" },
  { value: "CUSTOMER", label: "Customer" },
  { value: "CONFIRMATION_POST_PROCESSOR", label: "Post-confirm processor" },
  { value: "PORTAL_USER", label: "Portal user" },
]

const TRIGGERED_BY_LABEL = {
  AUTO_ON_NEW_STOP: "Automation",
  CUSTOMER: "Customer",
  CONFIRMATION_POST_PROCESSOR: "Post-confirm",
  PORTAL_USER: "Portal user",
}

const STATUS_LABEL = {
  REQUIRED: "New Request",
  REQUESTED: "Submitted",
  PENDING_APPROVAL: "Waiting For Approval",
  EXCEPTION: "Exception",
  CONFIG_ISU: "Configuration Issue",
  PRESCHEDULED: "Prescheduled",
  CONFIRMED: "Confirmed",
  NOTREQUIRED: "Not Required",
}

// Status badge colors mirror the dashboard: Exception/Config Issue = red, Confirmed = green,
// Submitted/Waiting = amber, New Request/Prescheduled = blue, everything else = grey.
const STATUS_COLOR = {
  CONFIRMED: "badge-soft-success",
  EXCEPTION: "badge-soft-danger",
  CONFIG_ISU: "badge-soft-danger",
  REQUESTED: "badge-soft-warning",
  PENDING_APPROVAL: "badge-soft-warning",
  REQUIRED: "badge-soft-info",
  PRESCHEDULED: "badge-soft-info",
  NOTREQUIRED: "badge-soft-secondary",
}

// Mirrors the backend RETRIGGERABLE_STATUSES - only these can be re-fired (Confirmed is shown but
// not selectable). The backend is authoritative and skips anything else.
const RETRIGGERABLE = ["REQUIRED", "REQUESTED", "PENDING_APPROVAL", "EXCEPTION", "CONFIG_ISU", "PRESCHEDULED"]
const isRetriggerable = r => RETRIGGERABLE.includes(r.currentStatus)

// Received-at shown in US Eastern (EST/EDT), DST-correct, no extra dependency.
const fmtEst = iso => {
  if (!iso) return "-"
  const p = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .formatToParts(new Date(iso))
    .reduce((a, x) => ((a[x.type] = x.value), a), {})
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`
}

const BulkRetrigger = () => {
  const [filter, setFilter] = useState("")
  const [fromDate, setFromDate] = useState(moment().startOf("day").format("YYYY-MM-DDTHH:mm"))
  const [toDate, setToDate] = useState(moment().endOf("day").format("YYYY-MM-DDTHH:mm"))
  const [triggeredByFilter, setTriggeredByFilter] = useState("")
  const [statusFilter, setStatusFilter] = useState("")

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)

  const [selectedIds, setSelectedIds] = useState([]) // appt_req_stop_log.id
  const [pendingRows, setPendingRows] = useState([])
  const [triggerMode, setTriggerMode] = useState("ORIGINAL")
  const [showConfirm, setShowConfirm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)

  const pageSize = 300

  const search = async () => {
    setLoading(true)
    setSearched(true)
    setSelectedIds([])
    setResult(null)
    try {
      const p = [`fromDate=${fromDate}`, `toDate=${toDate}`, `pageNumber=0`, `pageSize=${pageSize}`]
      if (filter.trim()) p.push(`filter=${encodeURIComponent(filter.trim())}`)
      if (triggeredByFilter) p.push(`triggeredBy=${encodeURIComponent(triggeredByFilter)}`)
      if (statusFilter) p.push(`status=${encodeURIComponent(statusFilter)}`)
      const resp = await get(`crs/retrigger-log?${p.join("&")}`)
      setRows(resp.content || [])
    } catch (e) {
      console.error("Stop-log search failed:", e)
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  const eligibleIds = rows.filter(isRetriggerable).map(r => r.id)
  const toggleOne = id =>
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  const allSelected = eligibleIds.length > 0 && eligibleIds.every(id => selectedIds.includes(id))
  const toggleAll = () => setSelectedIds(allSelected ? [] : eligibleIds)

  const openConfirm = ids => {
    const sel = rows.filter(r => ids.includes(r.id))
    if (!sel.length) return
    setPendingRows(sel)
    setTriggerMode("ORIGINAL")
    setResult(null)
    setShowConfirm(true)
  }

  // Preview of what each re-trigger will carry. The stop/order details (dates, address, refs) are
  // read live from the stop when the QSND fires, so we show the request context we have here plus
  // the trigger identity being replayed. refresh_movement is always false on a re-trigger.
  const previewPlan = () => ({
    triggerMode,
    refreshMovement: false,
    requests: pendingRows.map(r => ({
      logId: r.id,
      stopId: r.stopId,
      orderId: r.orderId,
      movementId: r.movementId,
      location: r.locationName || r.locationId,
      customerId: r.customerId,
      apptSystem: r.apptSystem,
      eventType: r.eventType,
      triggeredBy: triggerMode === "ORIGINAL" ? r.triggeredBy : triggerMode,
      triggeredReason: r.triggeredReason || null,
      capturedAt: r.receivedAt,
      currentStatus: r.currentStatus,
    })),
  })

  const submit = async () => {
    if (!pendingRows.length) return
    try {
      setSubmitting(true)
      setResult(null)
      const body = { logIds: pendingRows.map(r => r.id) }
      if (triggerMode !== "ORIGINAL") body.triggeredBy = triggerMode
      const resp = await post("crs/retrigger", body)
      setResult(resp)
      setSelectedIds([])
      // Do not refresh the list here - it would clear `result` and hide the success view.
      // The list is refreshed when the user closes the result (see the Close button below).
    } catch (e) {
      console.error("Retrigger failed:", e)
      setResult({ status: "error", message: e.message || "Something went wrong. Please try again." })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <h4 className="mb-3">Re-trigger Requests</h4>

        <Card>
          <CardBody>
            <Row className="g-2 align-items-end">
              <Col md={4}>
                <Label className="form-label">Search</Label>
                <Input
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  placeholder="Order #, location, customer, reference #, movement, appt system..."
                  onKeyDown={e => e.key === "Enter" && search()}
                />
              </Col>
              <Col md={2}>
                <Label className="form-label">
                  From (trigger time){" "}
                  <i
                    className="bx bx-info-circle"
                    title="Times you select are in EST (US Eastern)"
                    style={{ cursor: "help" }}
                  ></i>
                </Label>
                <Input type="datetime-local" value={fromDate} onChange={e => setFromDate(e.target.value)} />
              </Col>
              <Col md={2}>
                <Label className="form-label">
                  To{" "}
                  <i
                    className="bx bx-info-circle"
                    title="Times you select are in EST (US Eastern)"
                    style={{ cursor: "help" }}
                  ></i>
                </Label>
                <Input type="datetime-local" value={toDate} onChange={e => setToDate(e.target.value)} />
              </Col>
              <Col md={2}>
                <Label className="form-label">Triggered by</Label>
                <Input
                  type="select"
                  value={triggeredByFilter}
                  onChange={e => setTriggeredByFilter(e.target.value)}
                >
                  <option value="">Any</option>
                  {Object.keys(TRIGGERED_BY_LABEL).map(k => (
                    <option key={k} value={k}>
                      {TRIGGERED_BY_LABEL[k]}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={2}>
                <Label className="form-label">Status</Label>
                <Input type="select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                  <option value="">Any</option>
                  {Object.keys(STATUS_LABEL).map(k => (
                    <option key={k} value={k}>
                      {STATUS_LABEL[k]}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={2}>
                <Button color="primary" onClick={search} disabled={loading}>
                  {loading ? "Searching..." : "Search"}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {searched && (
          <Card>
            <CardBody>
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="text-muted">
                  {rows.length} request(s) · {selectedIds.length} selected
                </span>
                <Button
                  color="primary"
                  size="sm"
                  disabled={selectedIds.length === 0}
                  onClick={() => openConfirm(selectedIds)}
                >
                  Re-trigger selected
                </Button>
                {selectedIds.length > 0 && (
                  <Button color="link" size="sm" className="p-0" onClick={() => setSelectedIds([])}>
                    Clear
                  </Button>
                )}
              </div>

              <div className="table-responsive">
                <Table bordered hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 30 }}>
                        <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                      </th>
                      <th>
                        Received{" "}
                        <i
                          className="bx bx-info-circle"
                          title="Times shown are in EST (US Eastern)"
                          style={{ cursor: "help" }}
                        ></i>
                      </th>
                      <th>Order #</th>
                      <th>Stop</th>
                      <th>Movement</th>
                      <th>Location</th>
                      <th>Customer</th>
                      <th>Appt System</th>
                      <th>Status</th>
                      <th>Triggered By</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={11}>Loading...</td>
                      </tr>
                    )}
                    {!loading && rows.length === 0 && (
                      <tr>
                        <td colSpan={11}>No requests found for this search.</td>
                      </tr>
                    )}
                    {!loading &&
                      rows.map(r => (
                        <tr key={r.id}>
                          <td>
                            {isRetriggerable(r) ? (
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(r.id)}
                                onChange={() => toggleOne(r.id)}
                              />
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>{fmtEst(r.receivedAt)}</td>
                          <td>{r.orderId || "-"}</td>
                          <td>{r.stopId}</td>
                          <td>{r.movementId}</td>
                          <td>{r.locationName || r.locationId || "-"}</td>
                          <td>{r.customerId || "-"}</td>
                          <td>{r.apptSystem || "-"}</td>
                          <td>
                            <Badge className={STATUS_COLOR[r.currentStatus] || "badge-soft-secondary"}>
                              {STATUS_LABEL[r.currentStatus] || r.currentStatus || "-"}
                            </Badge>
                          </td>
                          <td>
                            {TRIGGERED_BY_LABEL[r.triggeredBy] || r.triggeredBy || "-"}
                            {r.triggeredReason ? ` (${r.triggeredReason})` : ""}
                          </td>
                          <td>
                            {isRetriggerable(r) && (
                              <Button
                                color="primary"
                                size="sm"
                                outline
                                onClick={() => openConfirm([r.id])}
                              >
                                Re-trigger
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        )}

        <Modal isOpen={showConfirm} toggle={() => setShowConfirm(false)} centered size="lg">
          <div className="modal-header">
            <h5 className="modal-title mt-0">Re-trigger {pendingRows.length} Request(s)</h5>
            <button
              type="button"
              className="close"
              aria-label="Close"
              onClick={() => setShowConfirm(false)}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            {!result && (
              <>
                <p>
                  Are you sure you want to re-trigger{" "}
                  <span className="text-primary">{pendingRows.length}</span> request(s)? Each is
                  resubmitted through scheduling and its timeline will show processing has started.
                </p>
                <Row className="mb-2">
                  <Col md={6}>
                    <Label className="form-label">Trigger as</Label>
                    <Input
                      type="select"
                      value={triggerMode}
                      onChange={e => setTriggerMode(e.target.value)}
                    >
                      {TRIGGER_MODES.map(m => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </Input>
                    <small className="text-muted">
                      "Original" replays each request's captured metadata verbatim so the rules
                      engine runs the same rules.
                    </small>
                  </Col>
                </Row>
                <details className="mb-2">
                  <summary className="text-muted" style={{ cursor: "pointer" }}>
                    View payload
                  </summary>
                  <small className="text-muted d-block mb-1">
                    Stop/order details (dates, address, references) are read live from the stop when
                    the request fires. Shown here is the request context and the trigger identity
                    being replayed.
                  </small>
                  <pre style={{ maxHeight: 260, overflow: "auto", background: "#f8f9fa", padding: 8 }}>
                    {JSON.stringify(previewPlan(), null, 2)}
                  </pre>
                </details>
              </>
            )}
            {result && result.status !== "error" && (
              <div className="text-center py-2">
                <i className="bx bx-check-circle text-success" style={{ fontSize: 44 }} />
                <h5 className="text-success mt-2 mb-1">{result.message}</h5>
                <p className="text-muted mb-1">
                  Sent: {result.sent} · Skipped: {result.skipped}
                </p>
                {Array.isArray(result.results) &&
                  result.results.some(r => r.status !== "sent") && (
                    <pre
                      style={{
                        maxHeight: 220,
                        overflow: "auto",
                        background: "#f8f9fa",
                        padding: 8,
                        textAlign: "left",
                      }}
                    >
                      {JSON.stringify(result.results.filter(r => r.status !== "sent"), null, 2)}
                    </pre>
                  )}
              </div>
            )}
            {result && result.status === "error" && (
              <div className="text-danger">{result.message}</div>
            )}
          </div>
          <div className="modal-footer">
            {!result ? (
              <>
                <Button
                  color="primary"
                  onClick={submit}
                  disabled={submitting || pendingRows.length === 0}
                >
                  {submitting ? "Submitting..." : "Confirm & Re-trigger"}
                </Button>
                <Button color="secondary" onClick={() => setShowConfirm(false)} disabled={submitting}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                color="secondary"
                onClick={() => {
                  setShowConfirm(false)
                  search()
                }}
              >
                Close
              </Button>
            )}
          </div>
        </Modal>
      </Container>
    </div>
  )
}

export default BulkRetrigger
